export const LOCATION_HEADERS = Object.freeze({
  state: 'Iglesia - Estado',
  city: 'Iglesia - Ciudad',
  churchName: 'Iglesia - Nombre',
  country: 'Pais',
});

function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFKC')
    .replace(/\u00A0/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase();
}

function trimValue(value) {
  return String(value ?? '').trim();
}

export function parseLocationCellValue(rawValue) {
  if (Array.isArray(rawValue)) {
    return rawValue.map((value) => trimValue(value));
  }

  const trimmedValue = trimValue(rawValue);

  if (!trimmedValue) {
    return [];
  }

  if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmedValue);

      if (Array.isArray(parsed)) {
        return parsed.map((value) => trimValue(value));
      }
    } catch {
      // Legacy single-value support.
    }
  }

  return [trimmedValue];
}

export function serializeLocationCellValue(values) {
  const normalizedValues = Array.isArray(values)
    ? values.map((value) => trimValue(value))
    : [];

  if (normalizedValues.length === 0) {
    return '';
  }

  if (normalizedValues.length === 1) {
    return normalizedValues[0] || '';
  }

  return JSON.stringify(normalizedValues);
}

export function normalizeIndexedLocationArrays(rawValuesByKey) {
  const arraysByKey = Object.entries(rawValuesByKey || {}).reduce((accumulator, [key, rawValue]) => {
    accumulator[key] = parseLocationCellValue(rawValue);
    return accumulator;
  }, {});

  const maxLength = Math.max(
    0,
    ...Object.values(arraysByKey).map((values) => values.length)
  );

  return Object.keys(LOCATION_HEADERS).reduce((accumulator, key) => {
    accumulator[key] = Array.from({ length: maxLength }, (_, index) => trimValue(arraysByKey[key]?.[index]));
    return accumulator;
  }, {});
}

export function getLocationColumnIndexes(headers) {
  return {
    stateIndex: (headers || []).findIndex((header) => normalizeText(header) === normalizeText(LOCATION_HEADERS.state)),
    cityIndex: (headers || []).findIndex((header) => normalizeText(header) === normalizeText(LOCATION_HEADERS.city)),
    churchNameIndex: (headers || []).findIndex((header) => normalizeText(header) === normalizeText(LOCATION_HEADERS.churchName)),
    countryIndex: (headers || []).findIndex((header) => normalizeText(header) === normalizeText(LOCATION_HEADERS.country)),
  };
}

export function getIndexedLocationsFromRow(headers, row) {
  const { stateIndex, cityIndex, churchNameIndex, countryIndex } = getLocationColumnIndexes(headers);

  if (stateIndex < 0 || cityIndex < 0 || countryIndex < 0) {
    return [];
  }

  const arraysByKey = normalizeIndexedLocationArrays({
    state: row?.[stateIndex],
    city: row?.[cityIndex],
    churchName: churchNameIndex >= 0 ? row?.[churchNameIndex] : '',
    country: row?.[countryIndex],
  });

  return arraysByKey.state.map((_, index) => ({
    index,
    state: arraysByKey.state[index] || '',
    city: arraysByKey.city[index] || '',
    churchName: arraysByKey.churchName[index] || '',
    country: arraysByKey.country[index] || '',
  })).filter((location) => (
    location.state || location.city || location.churchName || location.country
  ));
}

export function serializeIndexedLocations(locations) {
  const normalizedLocations = (Array.isArray(locations) ? locations : [])
    .map((location) => ({
      state: trimValue(location?.state),
      city: trimValue(location?.city),
      churchName: trimValue(location?.churchName),
      country: trimValue(location?.country),
    }))
    .filter((location) => (
      location.state || location.city || location.churchName || location.country
    ));

  return {
    state: serializeLocationCellValue(normalizedLocations.map((location) => location.state)),
    city: serializeLocationCellValue(normalizedLocations.map((location) => location.city)),
    churchName: serializeLocationCellValue(normalizedLocations.map((location) => location.churchName)),
    country: serializeLocationCellValue(normalizedLocations.map((location) => location.country)),
  };
}

export function applyIndexedLocationsToRowData(headers, rowData, locations) {
  const { stateIndex, cityIndex, churchNameIndex, countryIndex } = getLocationColumnIndexes(headers);

  if (stateIndex < 0 || cityIndex < 0 || countryIndex < 0) {
    return rowData;
  }

  const serializedLocations = serializeIndexedLocations(locations);
  const nextRowData = [...rowData];

  nextRowData[stateIndex] = serializedLocations.state;
  nextRowData[cityIndex] = serializedLocations.city;
  nextRowData[countryIndex] = serializedLocations.country;

  if (churchNameIndex >= 0) {
    nextRowData[churchNameIndex] = serializedLocations.churchName;
  }

  return nextRowData;
}

export function areLocationScopesEqual(leftLocation, rightLocation) {
  return (
    normalizeText(leftLocation?.state) === normalizeText(rightLocation?.state)
    && normalizeText(leftLocation?.city) === normalizeText(rightLocation?.city)
    && normalizeText(leftLocation?.churchName) === normalizeText(rightLocation?.churchName)
    && normalizeText(leftLocation?.country) === normalizeText(rightLocation?.country)
  );
}

export function formatLocationScopeLabel(location, options = {}) {
  const emptyLabel = options.emptyLabel || 'Sin sede';
  const parts = [
    trimValue(location?.country),
    trimValue(location?.state),
    trimValue(location?.city),
    trimValue(location?.churchName),
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' / ') : emptyLabel;
}

export function formatLocationScopeTooltip(locations) {
  return (Array.isArray(locations) ? locations : [])
    .map((location, index) => `${index + 1}. ${formatLocationScopeLabel(location)}`)
    .join('\n');
}

export function summarizeLocationCell(rawValue) {
  const values = parseLocationCellValue(rawValue).filter(Boolean);

  if (values.length <= 1) {
    return {
      count: values.length,
      label: values[0] || '',
    };
  }

  return {
    count: values.length,
    label: `${values.length}+`,
  };
}

export function findMatchingLocationScope(locations, targetLocation) {
  return (Array.isArray(locations) ? locations : []).find((location) => (
    areLocationScopesEqual(location, targetLocation)
  )) || null;
}

export function rowContainsLocationScope(headers, row, targetLocation) {
  if (!targetLocation) {
    return true;
  }

  return Boolean(findMatchingLocationScope(getIndexedLocationsFromRow(headers, row), targetLocation));
}

export function buildLocationCellFilterText(rawValue) {
  return parseLocationCellValue(rawValue)
    .filter(Boolean)
    .join(' | ');
}